// AI-разметка сюжета креативов (MYUA-16423 / бэкенд MYUA-16214).
//
// Данные лежат на бэкенде в `plot_recognition.PlotRecognitionResult` (OneToOne на `bl.Creative`)
// и отдаются вложенным объектом `plot_recognition` в `/bl/creatives`. Правятся тем же
// эндпоинтом: `PATCH /bl/creatives/<id>` с `{ plot_recognition: { <поле>: <значение> } }`.
//
// Важно: бэкенд помечает КАЖДОЕ присланное поле как отредактированное вручную
// (`user_edited_fields`) и навсегда перестаёт перезаписывать его данными ML-пайплайна.
// Поэтому в PATCH отправляем только реально изменённые поля — см. `diffPlotRecognition`.

/** Статусы распознавания на бэкенде: `plot_recognition.PlotRecognitionStatus`. */
export const PLOT_RECOGNITION_STATUSES = ['pending', 'processing', 'success', 'error'] as const;

export type PlotRecognitionStatus = (typeof PLOT_RECOGNITION_STATUSES)[number];

/** Подписи статусов для столбца `Recognition` в файл-менеджере (MYUA-16736). */
export const PLOT_RECOGNITION_STATUS_LABELS: Record<PlotRecognitionStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  success: 'Success',
  error: 'Error',
};

/**
 * Статус с ноды листинга: бэкенд может прислать значение, которого фронт ещё не знает,
 * поэтому приводим неизвестное к `null` (в интерфейсе — как «нет разметки»).
 */
export const asPlotRecognitionStatus = (value: unknown): PlotRecognitionStatus | null =>
  PLOT_RECOGNITION_STATUSES.includes(value as PlotRecognitionStatus)
    ? (value as PlotRecognitionStatus)
    : null;

export type NarrativeStage = { name: string; description: string };

/** `{"type": "Problem → Solution → Reward", "stages": [{name, description}]}`; на бэкенде JSONField. */
export type NarrativeStructure = {
  type?: string;
  stages?: NarrativeStage[];
};

/** Вложенный `plot_recognition` из `CreativeSerializer`. `null`, если распознавания не было. */
export type PlotRecognitionServer = {
  status: PlotRecognitionStatus;
  error: string;
  analyst_insight: string;
  suggested_tags: string[];
  characters: string[];
  locations: string[];
  key_objects: string[];
  hook: string;
  story_narrative: string;
  psychological_trigger: string;
  narrative_structure: NarrativeStructure | null;
  /** Поля, правленные руками: ML их больше не перезапишет. */
  user_edited_fields: string[];
};

/**
 * Подмножество полей распознавания, которое выводим во вкладку `Metadata`
 * (по постановке: Characters, Locations, Key objects, Narrative structure, Analyst insight).
 */
export type PlotRecognition = {
  characters: string[];
  locations: string[];
  key_objects: string[];
  narrative_structure: NarrativeStructure;
  analyst_insight: string;
};

export type PlotRecognitionField = keyof PlotRecognition;

export const PLOT_RECOGNITION_FIELDS: PlotRecognitionField[] = [
  'characters',
  'locations',
  'key_objects',
  'narrative_structure',
  'analyst_insight',
];

export const PLOT_RECOGNITION_LABELS: Record<PlotRecognitionField, string> = {
  characters: 'Characters',
  locations: 'Locations',
  key_objects: 'Key objects',
  narrative_structure: 'Narrative structure',
  analyst_insight: 'Analyst insight',
};

/** Поля-списки: в интерфейсе — визуальные теги. */
export const PLOT_RECOGNITION_LIST_FIELDS = ['characters', 'locations', 'key_objects'] as const;

export const EMPTY_VALUE_PLACEHOLDER = 'empty';

/** Справочная информация о распознавании для шапки вкладки `Metadata`. */
export type PlotRecognitionInfo = {
  status: PlotRecognitionStatus | null;
  error: string;
  /** Поля, зафиксированные вручную: ML их больше не перезапишет. */
  userEditedFields: PlotRecognitionField[];
};

export const getPlotRecognitionInfo = (
  pr: PlotRecognitionServer | null | undefined,
): PlotRecognitionInfo => ({
  status: pr?.status ?? null,
  error: pr?.error ?? '',
  userEditedFields: PLOT_RECOGNITION_FIELDS.filter((field) =>
    (pr?.user_edited_fields ?? []).includes(field),
  ),
});

export const emptyPlotRecognition = (): PlotRecognition => ({
  characters: [],
  locations: [],
  key_objects: [],
  narrative_structure: {},
  analyst_insight: '',
});

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];

const asNarrativeStructure = (value: unknown): NarrativeStructure => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const raw = value as Record<string, unknown>;
  const stages = Array.isArray(raw.stages)
    ? raw.stages
        .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
        .map((s) => ({
          name: typeof s.name === 'string' ? s.name : '',
          description: typeof s.description === 'string' ? s.description : '',
        }))
    : undefined;
  return {
    ...raw,
    type: typeof raw.type === 'string' ? raw.type : undefined,
    stages,
  };
};

/** Приводит ответ сервера к редактируемой форме; `null`/мусор → пустые значения. */
export const getPlotRecognition = (
  pr: PlotRecognitionServer | null | undefined,
): PlotRecognition => {
  if (!pr) return emptyPlotRecognition();
  return {
    characters: asStringArray(pr.characters),
    locations: asStringArray(pr.locations),
    key_objects: asStringArray(pr.key_objects),
    narrative_structure: asNarrativeStructure(pr.narrative_structure),
    analyst_insight: typeof pr.analyst_insight === 'string' ? pr.analyst_insight : '',
  };
};

/**
 * Текстовая форма нарративной структуры: первая строка — название арки, далее `Стадия: описание`.
 * Используется и для read-only вывода, и как содержимое редактируемого текстового поля.
 */
export const narrativeStructureToText = (ns: NarrativeStructure): string => {
  const lines: string[] = [];
  if (ns.type) lines.push(ns.type);
  (ns.stages ?? []).forEach((stage) => {
    const name = stage.name.trim();
    const description = stage.description.trim();
    if (!name && !description) return;
    lines.push(name && description ? `${name}: ${description}` : name || description);
  });
  return lines.join('\n');
};

/** Обратное преобразование текста в JSON-структуру; неизвестные ключи исходника сохраняем. */
export const narrativeStructureFromText = (
  text: string,
  original: NarrativeStructure,
): NarrativeStructure => {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const { type: _type, stages: _stages, ...rest } = original;
  if (lines.length === 0) return { ...rest };
  const [typeLine, ...stageLines] = lines;
  const stages = stageLines.map((line) => {
    const separator = line.indexOf(':');
    if (separator === -1) return { name: '', description: line };
    return {
      name: line.slice(0, separator).trim(),
      description: line.slice(separator + 1).trim(),
    };
  });
  return { ...rest, type: typeLine, ...(stages.length > 0 ? { stages } : {}) };
};

const sameStringArray = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/** Сериализация с сортировкой ключей: порядок полей в JSON не должен давать ложный diff. */
const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
};

const sameNarrativeStructure = (a: NarrativeStructure, b: NarrativeStructure) =>
  stableStringify(a) === stableStringify(b);

/**
 * Только изменённые поля — их бэкенд помечает как `user_edited_fields` и блокирует
 * от машинных обновлений. Пустой объект = ничего не менялось, PATCH-поле не отправляем.
 */
export const diffPlotRecognition = (
  original: PlotRecognition,
  current: PlotRecognition,
): Partial<PlotRecognition> => {
  const diff: Partial<PlotRecognition> = {};
  PLOT_RECOGNITION_LIST_FIELDS.forEach((field) => {
    if (!sameStringArray(original[field], current[field])) diff[field] = current[field];
  });
  if (!sameNarrativeStructure(original.narrative_structure, current.narrative_structure)) {
    diff.narrative_structure = current.narrative_structure;
  }
  if (original.analyst_insight !== current.analyst_insight) {
    diff.analyst_insight = current.analyst_insight;
  }
  return diff;
};
